/**
 * Tasks component module.
 * @module shared/components/Tasks
 *
 */
import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import {Checkbox, Column, DescriptiveText, Icon, Image, LoadingSpinner, Row} from '../../../shared';
import {ColorBox} from '../../../shared/utils/colors';
import {formatter, parseErrorMessage} from '../../../shared/utils/utils';
import axiosInstance from '../../../shared/utils/axios';
import {_setNotification} from '../../../shared/reducers/headerNotification';
import history from '../../../history';
import {withTheme} from 'styled-components/macro';
import {PORTAL_NAME} from '../../../config';

const Tasks = ({advisorID, householdID, setNotification, session, theme}) => {
  const taskPriorityColors = {
    1: theme.dark ? '#FFCC03' : '#CF790B',
    2: theme.dark ? '#FFCC03' : '#CF790B',
    3: theme.dark ? '#99FA66' : '#79AF30',
    4: theme.dark ? '#99FA66' : '#79AF30',
    5: '#9F3D93',
  };
  const {data: tasks, mutate, error} = useSWR(
    `/advisors/${advisorID}/tasks${householdID ? `?householdID=${householdID}` : ''}`,
    url => axiosInstance.get(url).then(res => res.data)
  );

  const goTo = (partner, target) => {
    if (partner !== 'riab' || session.profile.id !== advisorID) {
      return;
    }
    return history.push(`/riab?target=${target}`);
  };

  const completeTask = task => {
    if (!task.complete) {
      const nt = [...tasks.map(y => ({...y}))];
      const t = nt.find(n => n.vendorID === task.vendorID && n.partner === task.partner);
      t.complete = true;
      mutate(nt, false);
      return axiosInstance.put(`/tasks/${task.partner}/${task.vendorID}/complete`)
        .then(() => setNotification({message: 'Task Marked Complete'}))
        .catch(err => setNotification({message: parseErrorMessage(err)}));
    }
  };

  const formattedTasks = React.useMemo(() => tasks && tasks.map(task => <Row key={'task' + task.vendorID}
                                                                             action={PORTAL_NAME !== 'widgets'
                                                                             && task.partner === 'riab'
                                                                             && (() => goTo(task.partner, task.type))}>
    {task.partner !== 'riab' && <Checkbox width={'auto'}
                                          dataPoint={{name: 'complete', value: !!task.complete}}
                                          onChange={() => completeTask(task)}/>}
    {task.partner === 'riab' && <Image src={'https://media.circleblack.com/icons/go_to_icon_small.svg'}
                                       maxWidth={'20px'}
                                       maxHeight={'20px'}/>}
    <Column width={'50px'} layout={{grow: 1}}>
      <DescriptiveText alignment={'left'}>{task.subject}</DescriptiveText>
      {task.partner !== 'riab' && <DescriptiveText alignment={'left'}>Client: {task.client
        ? `${task.client.firstName} ${task.client.lastName}`
        : 'None'}</DescriptiveText>}
      {task.due && <DescriptiveText alignment={'left'}>Due: {formatter('date')(task.due)}</DescriptiveText>}
    </Column>
    <ColorBox color={taskPriorityColors[task.priority] || taskPriorityColors[5]}/>
  </Row>), [tasks]);

  if (error) {
    return <DescriptiveText> There was an issue loading your tasks. If the issue persists please contact Client
      Services at {session.profile.brand.advisorCSEmail} or {formatter('phone')(session.profile.brand.advisorCSPhone)}.
    </DescriptiveText>;
  }

  if (!tasks) {
    return <LoadingSpinner/>;
  }

  if (!tasks.length) {
    return <Row>
      <Icon
        height='20px'
        width='20px'
        svg='thumbsUp'
      />
      <DescriptiveText>You do not currently have any tasks</DescriptiveText>
    </Row>;
  }

  return <Column showDividers={true} maxHeight={'100%'}>
    {formattedTasks}
  </Column>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  setNotification: modalToOpen => dispatch(_setNotification(modalToOpen)),
});

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(Tasks));

Tasks.propTypes = {
  /**
   * The ID for the advisor who's tasks we are getting, not necessarily the same user logged in
   */
  advisorID: PropTypes.number.isRequired,
  /**
   * The ID for household, certain partners allows gathering tasks for specific household
   */
  householdID: PropTypes.number,
  /**
   * User Session, includes token and email.
   */
  session: PropTypes.object,
  /**
   * Notification for Task Completed confirmation / err in header. Takes object with message property.
   */
  setNotification: PropTypes.func,
  theme: PropTypes.object,
};
